import React from "react";
import { Table, Tag, Space, Switch, Popconfirm, Tooltip } from "antd";
import Icon, {
  DeleteOutlined,
  EditOutlined,
  UserAddOutlined,
} from "@ant-design/icons";

const tagStyles = {
  padding: "2px 8px",
  borderRadius: 4,
  fontSize: 12,
  fontWeight: 500,
  marginRight: 4,
  fontFamily: "Inter",
  marginBottom: 4,
};

const channelColors = {
  EMAIL: { color: "#096dd9", bg: "#e6f7ff" },
  SMS: { color: "#ad4e00", bg: "#fff7e6" },
};

export const getChannelTagStyle = (channel) => {
  const colors = channelColors[channel] || { color: "#000", bg: "#f5f5f5" };
  return {
    backgroundColor: colors.bg,
    color: colors.color,
    padding: "2px 8px",
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 500,
    marginRight: 4,
    fontFamily: "Inter",
  };
};

export default function NotificationTopicsTable({
  topics,
  loading,
  onEdit,
  onDelete,
  onToggleEnable,
  onManageAssignments,
  pagination,
  onChange,
}) {

  console.log("Topics ===>", topics);
  const columns = [
    {
      title: "Topic Name",
      dataIndex: "name",
      key: "name",
      ellipsis: { showTitle: true },
      render: (text) => <span style={{ fontSize: 13 }}>{text}</span>,
    },
    {
      title: "Enabled",
      dataIndex: "enabled",
      key: "enabled",
      render: (enabled, record) => (
        <Switch
          checked={!!enabled}
          onChange={(checked) => onToggleEnable(record.id, checked)}
        />
      ),
    },
    {
      title: "Channels",
      dataIndex: "channels",
      key: "channels",
      render: (channels) => (
        <div>
          {channels.map((ch) => (
            <Tooltip key={ch.id} title={ch.description}>
              <Tag style={getChannelTagStyle(ch.type)}>{ch.type}</Tag>
            </Tooltip>
          ))}
        </div>
      ),
    },
    {
      title: "Roles",
      dataIndex: "roles",
      key: "roles",
      render: (roles) => {
        if (!roles || roles.length === 0)
          return <span style={{ color: "#888" }}>No roles</span>;
        const display = roles
          .slice(0, 2)
          .map((r) => r.name)
          .join(", ");
        const remaining = roles.length - 2;
        return (
          <span style={{ fontSize: 12, fontFamily: "Inter" }}>
            {display}
            {remaining > 0 && ` +${remaining} more`}
          </span>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <EditOutlined
            style={{ cursor: "pointer", marginRight: 5 }}
            onClick={() => onEdit(record)}
          />

          <Popconfirm
            title="Are you sure?"
            onConfirm={() => onDelete(record.id)}
          >
            <DeleteOutlined
              color="red"
              style={{ cursor: "pointer", marginRight: 5 }}
            />
          </Popconfirm>

          <UserAddOutlined onClick={() => onManageAssignments(record)} />
        </Space>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={topics}
      loading={loading}
      rowKey="id"
      size="small"
      expandable={{
        expandedRowRender: (record) => (
          <div style={{ padding: 8 }}>
            <p className="mb-2 pb-0">
              <strong>Description:</strong> {record.description}
            </p>
            <p className="mb-2 pb-0">
              <strong>Enabled:</strong> {record.enabled ? "Yes" : "No"}
            </p>

            <div
              style={{ display: "flex", alignItems: "center", gap: 4 }}
              className="mb-2"
            >
              <p className="mb-0 pb-0">
                <strong>Channels:</strong>
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {record.channels.length > 0 ? (
                  record.channels.map((ch) => (
                    <Tooltip key={ch.id} title={ch.description}>
                      <Tag style={getChannelTagStyle(ch.type)}>{ch.type}</Tag>
                    </Tooltip>
                  ))
                ) : (
                  <span style={{ color: "#888" }}>No channels</span>
                )}
              </div>
            </div>

            <div
              style={{ display: "flex", alignItems: "center", gap: 4 }}
              className="mb-2"
            >
              <p className="mb-0 pb-0">
                <strong>Roles:</strong>
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {record.roles.length > 0 ? (
                  record.roles.map((r) => (
                    <Tooltip key={r.id} title={r.description}>
                      <Tag color="#222" style={tagStyles}>
                        {r.name}
                      </Tag>
                    </Tooltip>
                  ))
                ) : (
                  <span style={{ color: "#888" }}>No roles assigned</span>
                )}
              </div>
            </div>

            <div
              style={{ display: "flex", alignItems: "center", gap: 4 }}
              className="mb-2"
            >
              <p className="mb-0 pb-0">
                <strong>Users:</strong>
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {record.users.length > 0 ? (
                  record.users.map((u) => (
                    <Tooltip key={u.id} title={u.description}>
                      <Tag color="#000" style={tagStyles}>
                        {u.firstName} {u.lastName}
                      </Tag>
                    </Tooltip>
                  ))
                ) : (
                  <span style={{ color: "#888" }}>No users assigned</span>
                )}
              </div>
            </div>
            <p>
              <strong>Template:</strong>{" "}
              {record.template ? (
                record.template.code
              ) : (
                <span style={{ color: "#888" }}>No template assigned</span>
              )}
            </p>
          </div>
        ),
        rowExpandable: (record) =>
          (record.users && record.users.length > 0) ||
          (record.roles && record.roles.length > 0) ||
          (record.channels && record.channels.length > 0) ||
          record.description ||
          record.template.code,
      }}
      className="custom-table"
      pagination={pagination}
      onChange={onChange}
    />
  );
}
